if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[type="submit"]');
      if (!this.form || !this.submitButton) return;
      this.form.querySelector('[name="id"]').disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (this.cart) this.submitButton.setAttribute('aria-haspopup', 'dialog');
    }

    disconnectedCallback() {
      clearTimeout(this.successTimeout);
    }

    getSubmitButtons() {
      // La barra usa el mismo formulario; otras fichas o compras rápidas quedan aisladas.
      return Array.from(document.querySelectorAll('button[type="submit"]'))
        .filter((button) => button.form === this.form);
    }

    setButtonState(state) {
      this.getSubmitButtons().forEach((button) => {
        const label = button.querySelector('.product-form-button-text') || button.querySelector('span');
        let loading = button.querySelector('.product-form-button-loading');
        let success = button.querySelector('.product-added-success');
        if (!loading) {
          loading = document.createElement('span');
          loading.className = 'product-form-button-loading hidden';
          loading.style.cssText = 'position: relative; z-index: 1; color: ' + getComputedStyle(label || button).color;
          loading.textContent = 'Agregando…';
          button.append(loading);
        }
        if (!success) {
          success = document.createElement('span');
          success.className = 'product-added-success hidden';
          success.style.cssText = 'position: relative; z-index: 1; color: ' + getComputedStyle(label || button).color;
          success.textContent = 'Agregado al carrito ✓';
          button.append(success);
        }
        label?.classList.toggle('hidden', state !== 'idle');
        loading.classList.toggle('hidden', state !== 'loading');
        success.classList.toggle('hidden', state !== 'success');
        // El estado textual evita superponer el spinner en las tarjetas de compra rápida.
        button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        button.classList.toggle('loading', state === 'loading');
        if (state === 'loading') {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
        } else {
          button.removeAttribute('aria-disabled');
          button.removeAttribute('aria-busy');
        }
      });
    }

    announce(message) {
      let status = this.querySelector('[data-cart-status]');
      if (!status) {
        status = document.createElement('p');
        status.className = 'visually-hidden';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.setAttribute('data-cart-status', '');
        this.append(status);
      }
      status.textContent = message;
    }

    getRequestedItems(formData) {
      const single = { id: String(formData.get('id') || ''), quantity: Number(formData.get('quantity') || 1), properties: {}, selling_plan: formData.get('selling_plan') || '' };
      const indexedItems = new Map();
      for (const [key, value] of formData.entries()) {
        const property = key.match(/^properties\[(.+)\]$/);
        if (property && value !== '') single.properties[property[1]] = value;
        const match = key.match(/^items\[(\d+)\]\[(id|quantity|selling_plan|properties)\](?:\[(.+)\])?$/);
        if (!match) continue;
        const item = indexedItems.get(match[1]) || { quantity: 1, properties: {}, selling_plan: '' };
        if (match[2] === 'properties') {
          if (value !== '') item.properties[match[3]] = value;
        } else {
          item[match[2]] = match[2] === 'quantity' ? Number(value) : String(value);
        }
        indexedItems.set(match[1], item);
      }
      return single.id ? [single] : Array.from(indexedItems.values());
    }

    getAddedDetail(response, requestedItems, source) {
      let remaining = requestedItems.slice();
      const items = [];
      const responseItems = Array.isArray(response.items) ? response.items : [response];
      responseItems.forEach((item) => {
        const variantId = item.variant_id || item.id;
        const properties = item.properties || {};
        const plan = String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
        let matches = remaining.filter((requested) => {
          if (requested.id !== String(variantId) || String(requested.selling_plan || '') !== plan) return false;
          const requestedProperties = requested.properties || {};
          const keys = Object.keys(requestedProperties);
          return keys.length === Object.keys(properties).length && keys.every((key) =>
            // Los archivos se convierten en URL al subirlos; conservamos la clave para emparejarlos.
            typeof requestedProperties[key] !== 'string' ? key in properties : requestedProperties[key] === properties[key]
          );
        });
        if (!matches.length) {
          const fallback = remaining.find((requested) => requested.id === String(variantId));
          if (fallback) matches = [fallback];
        }
        if (!matches.length) return;
        remaining = remaining.filter((requested) => !matches.includes(requested));
        // Shopify puede fusionar entradas y devolver cantidades acumuladas: contamos solo lo enviado.
        items.push({
          variant_id: variantId,
          product_title: item.product_title || item.title,
          price: Number(item.final_price ?? item.discounted_price ?? item.price) / 100,
          quantity: matches.reduce((quantity, requested) => quantity + requested.quantity, 0)
        });
      });
      return {
        source,
        items,
        value: Math.round(items.reduce((total, item) => total + item.price * item.quantity, 0) * 100) / 100,
        currency: 'UYU'
      };
    }

    async onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.pending || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true') return;
      const source = evt.submitter?.dataset.cartSource === 'sticky' ? 'sticky' : 'product-form';
      clearTimeout(this.successTimeout);
      this.error = false;
      this.pending = true;
      this.handleErrorMessage();
      this.setButtonState('loading');
      this.announce('Agregando…');

      let response;
      try {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        const formData = new FormData(this.form);
        const quantityInput = Array.from(this.form.elements).find((input) =>
          input.name === 'quantity' && input.classList.contains('quantity__input') && input.type !== 'hidden'
        ) || this.form.querySelector('.product_quantity_input');
        if (quantityInput) formData.set('quantity', quantityInput.value);
        // Las propiedades opcionales que quedaron vacías (por ejemplo, «Indicaciones») no se mandan al pedido.
        const propiedadesVacias = new Map();
        for (const [key, value] of formData.entries()) {
          if (!key.startsWith('properties[')) continue;
          const vacia = typeof value === 'string' ? value.trim() === '' : !(value && value.size);
          propiedadesVacias.set(key, (propiedadesVacias.has(key) ? propiedadesVacias.get(key) : true) && vacia);
        }
        propiedadesVacias.forEach((vacia, key) => { if (vacia) formData.delete(key); });
        // Los packs conservan sus items y propiedades; no se envía además el ID combinado.
        const variantInput = this.form.querySelector('[name="id"]');
        if (variantInput.value.includes(',')) {
          formData.delete('id');
          formData.delete('quantity');
        }
        const requestedItems = this.getRequestedItems(formData);
        if (!requestedItems.length || requestedItems.some((item) => !item.id || !Number.isInteger(item.quantity) || item.quantity < 1)) {
          throw new Error('Revisá la cantidad disponible para esta opción.');
        }
        if (this.cart) {
          formData.set('sections', this.cart.getSectionsToRender().map((section) => section.id).join(','));
          formData.set('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;
        this.skipCart = variantInput.dataset.skipCart === 'true';
        const result = await fetch(routes.cart_add_url, config);
        response = await result.json();
        if (!result.ok || response.status) {
          const description = response.description || response.message;
          throw new Error(typeof description === 'string' ? description : '');
        }
        const addedItems = Array.isArray(response.items) ? response.items : [response];
        if (!addedItems.length || addedItems.some((item) => !(item.variant_id || item.id))) {
          throw new Error('No recibimos la confirmación del carrito. Revisalo antes de volver a intentar.');
        }
        document.dispatchEvent(new CustomEvent('tematik:cart:added', {
          detail: this.getAddedDetail(response, requestedItems, source)
        }));
        this.setButtonState('success');
        this.announce('Agregado al carrito ✓');
        this.successTimeout = setTimeout(() => this.setButtonState('idle'), 2500);
      } catch (error) {
        this.error = true;
        const message = 'No pudimos agregarlo al carrito. Volvé a intentar.' +
          (error.message && !(error instanceof TypeError) && !(error instanceof SyntaxError) ? ' ' + error.message : '');
        this.setButtonState('idle');
        this.handleErrorMessage(message);
        this.announce('');
        document.dispatchEvent(new CustomEvent('tematik:cart:error', { detail: { source, message } }));
      } finally {
        // La limpieza nunca confirma una compra ni borra la selección del cliente.
        this.pending = false;
        this.getSubmitButtons().forEach((button) => {
          button.classList.remove('loading');
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
        });
      }
      if (this.error) return;

      // Un problema al dibujar el drawer no convierte un agregado confirmado en un error de compra.
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form' });
        if (this.skipCart) {
          window.location.assign('/checkout');
        } else if (!this.cart) {
          window.location.assign(routes.cart_url);
        } else {
          this.cart.classList.remove('is-empty');
          const renderCart = () => {
            try {
              this.cart.renderContents(response);
            } catch (error) {
              window.location.assign(routes.cart_url);
            }
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => setTimeout(renderCart), { once: true });
            quickAddModal.hide(true);
          } else {
            renderCart();
          }
        }
      } catch (error) {
        window.location.assign(this.skipCart ? '/checkout' : routes.cart_url);
      }
    }

    handleErrorMessage(errorMessage = false) {
      let wrapper = this.querySelector('.product-form__error-message-wrapper');
      if (!wrapper) {
        wrapper = document.createElement('div');
        wrapper.className = 'product-form__error-message-wrapper';
        wrapper.setAttribute('role', 'alert');
        const text = document.createElement('span');
        text.className = 'product-form__error-message';
        wrapper.append(text);
        this.prepend(wrapper);
      }
      wrapper.hidden = !errorMessage;
      wrapper.querySelector('.product-form__error-message').textContent = errorMessage || '';
      this.getSubmitButtons().forEach((button) => {
        const stickyError = button.closest('.sticky-atc')?.querySelector('[data-cart-error]');
        if (!stickyError) return;
        stickyError.hidden = !errorMessage;
        stickyError.textContent = errorMessage || '';
      });
    }
  });
}
